import { Hub, HubCallback } from '@aws-amplify/core';
import { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';
import $$api from 'models/api';
import { storage } from 'models/store-global';

import EstatePlanService from 'services/EstatePlanService';
import QuestionsService from 'services/QuestionsService';

import { MyStore } from 'types/Store';
import { Answers } from 'types/zod/answers';
import { type Question } from 'types/zod/question';

export const LEARNING_CENTER_CHANNEL = 'learningCenterChannel';
export const SHOW_FOR_QUESTION = 'showForQuestion';
export const SHOW_FOR_LABEL = 'showForLabel';

const service = new QuestionsService();

export interface Token {
    text: string;
    helpArticleLabel: string | null;
}

const GuideService = {
    wordsMap: null as Record<string, string> | null,

    currentQuestionLabel: '',

    startListening: (listener: HubCallback) => {
        Hub.listen(LEARNING_CENTER_CHANNEL, listener);
    },

    stopListening: (listener: HubCallback) => {
        Hub.remove(LEARNING_CENTER_CHANNEL, listener);
    },

    setQuestionInstructions: (newValue: string) => {
        storage.set((store): MyStore => ({ ...store, questionInstructions: newValue }));
    },

    getFirstAvailableArticle: async (labels: string[]) => {
        try {
            return await $$api.guide.getFirstAvailableArticleFx(labels);
        } catch (e) {
            return null;
        }
    },

    getArticleByLabelAndContext: async (label: string, context: string[]) => {
        const contextCopy = context.slice();
        const labels = [];
        for (let i = 0; i <= context.length; i++) {
            const labelWithContext = GuideService.getLabelWithContext(label, contextCopy);
            labels.push(labelWithContext);
            contextCopy.pop();
        }

        return await GuideService.getFirstAvailableArticle(labels);
    },

    getLabelWithContext: (label: string, context: string[]) => {
        if (!context.length) {
            return label;
        }
        return context.join('.') + '.' + label;
    },

    showHelpForQuestion: async (question: Question, questionLabel: string, forceShow?: boolean) => {
        if (questionLabel === GuideService.currentQuestionLabel) {
            return;
        }
        GuideService.currentQuestionLabel = questionLabel;

        const productCode = question?._help?.product_code ? EstatePlanService.getSelectedProductContext() : '';
        const questionContext = productCode ? [productCode] : [];
        questionContext.push(...(question._fullContext || []));
        if (questionLabel) {
            const value = await GuideService.getArticleByLabelAndContext(questionLabel, questionContext);
            if (value != null) {
                GuideService.setQuestionInstructions(value);
            } else {
                GuideService.setQuestionInstructions('');
            }
        } else {
            GuideService.setQuestionInstructions('');
        }

        GuideService.dispatchShowForQuestionEvent(forceShow);
    },

    dispatchShowForQuestionEvent: (forceShow?: boolean) => {
        Hub.dispatch(LEARNING_CENTER_CHANNEL, {
            event: SHOW_FOR_QUESTION,
            message: '',
            data: {
                forceShow: forceShow,
            },
        });
    },

    showHelpForLabel: (label: string) => {
        Hub.dispatch(LEARNING_CENTER_CHANNEL, {
            event: SHOW_FOR_LABEL,
            message: '',
            data: {
                label: label,
            },
        });
    },

    getWordsMap: (t: TFunction): Record<string, string> => {
        if (GuideService.wordsMap !== null) {
            return GuideService.wordsMap;
        } else {
            GuideService.wordsMap = {} as Record<string, string>;
            const labels = t('learningCenter:_labels', { returnObjects: true }) as string[];
            if (Array.isArray(labels)) {
                labels.forEach((label) => {
                    const words = t('learningCenter:' + label, { returnObjects: true }) as string[];
                    words.forEach((word) => {
                        GuideService.wordsMap![word] = label;
                    });
                });
            }
            return GuideService.wordsMap;
        }
    },

    tokenize: (text: string, wordsMap: Record<string, string>): Token[] => {
        // tokens contains text splitted in parts. Some parts will have an associated helpArticleLabel.
        // It gets initialized as a full text and then is being iteratively splitted into smaller parts.
        let tokens = [{ text: text, helpArticleLabel: null }] as Token[];
        const wordsMapEntries = Object.entries(wordsMap);
        // Sorting is needed for the longest key tp go first.
        wordsMapEntries.sort((a, b) => b[0].length - a[0].length);
        for (const key of wordsMapEntries.map((entry) => entry[0])) {
            // Only a single link to the same help article is allowed.
            if (tokens.map((token) => token.helpArticleLabel).indexOf(wordsMap[key]) > -1) {
                continue;
            }
            // Below is the regex that matches whole word or expression.
            // For example, if key is "trust" then "trustee" won't be matched.
            const regexpKey = new RegExp(`(^|\\b)(${key})(\\b|$)`, 'i');
            const newArr = [];
            for (const labelObj of tokens) {
                if (labelObj.helpArticleLabel !== null) {
                    newArr.push(labelObj);
                } else {
                    const curStr = labelObj.text;
                    // This is where split is happening.
                    const splitted = curStr.split(regexpKey).filter((part) => part !== '');
                    if (splitted.length === 1) {
                        newArr.push({
                            helpArticleLabel: splitted[0].toLowerCase() === key.toLowerCase() ? wordsMap[key] : null,
                            text: splitted[0],
                        });
                        continue;
                    }

                    if (splitted[0].toLowerCase() === key.toLowerCase()) {
                        newArr.push({ helpArticleLabel: wordsMap[key], text: splitted[0] });
                        if (splitted.length > 1) {
                            splitted.shift();
                            newArr.push({ helpArticleLabel: null, text: splitted.join('') });
                        }
                    } else {
                        newArr.push({ helpArticleLabel: null, text: splitted[0] });
                        newArr.push({ helpArticleLabel: wordsMap[key] as string, text: splitted[1] });
                        if (splitted.length > 2) {
                            splitted.shift();
                            splitted.shift();
                            newArr.push({ helpArticleLabel: null, text: splitted.join('') });
                        }
                    }
                }
            }
            tokens = newArr;
        }
        return tokens;
    },

    getArticleLabelForQuestion: (
        question: Question,
        answersForConditions: Answers,
        stateCode: any = null,
        suffix?: string,
    ): string => {
        let questionId = service.getQuestionId(question, answersForConditions);
        if (suffix) {
            questionId = questionId + '_' + suffix;
        }

        const matchingStateGroup = (question._help?.state_groups || []).find((stateGroup) => {
            return stateGroup.includes(stateCode);
        });
        let stateSuffix = '';
        if (matchingStateGroup) {
            stateSuffix = '-' + matchingStateGroup;
        } else if (question?._help?.state) {
            stateSuffix = '-' + stateCode;
        }

        const widgetType = question._widget?.type || '';
        if (widgetType !== 'radio' && widgetType !== 'check') return `${questionId}${stateSuffix}`;

        const conditional = !!question?._help?.conditional;
        if (!conditional) return `${questionId}${stateSuffix}`;

        const innerQuestions = [] as Question[];
        Object.keys(question).forEach((key) => {
            if (!service.isSpecialField(key)) {
                const innerQuestion = cloneDeep(question[key]) as Question;
                innerQuestions.push(innerQuestion);
            }
        });
        const possibleAnswersIndices: number[] = innerQuestions
            .map((innerQuestion, index) =>
                service.checkCondition(innerQuestion, answersForConditions) ? index + 1 : -1,
            )
            .filter((value) => value !== -1);

        return `${questionId}-${possibleAnswersIndices.join('')}${stateSuffix}`;
    },
};

export default GuideService;
